/**
 * DateRange - a slider for dates
 *
 * The logic that flows throughout the whole component is as follow:
 *
 * start means lastDay minus the total number of days (props.days)
 * lastDay is today
 *
 */
import React from "react";
import Handle from "rc-slider/lib/Handle";
import cns from "classnames";
import RCRange from "rc-slider/lib/Range";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import debug from "debug";
import { withStyles } from "@material-ui/core";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Tooltip from "@material-ui/core/Tooltip";
import Popover from "@material-ui/core/Popover";
import InputLabel from "@material-ui/core/InputLabel";

import { Classes, ThemeType } from "@theme";

import { Small } from "../typography/Small";
import { dateToValue, valueToDateString, pickerDateToDate, valueToDate } from "./utils";

import "rc-slider/assets/index.css";

const log = debug("app:components:Range");

type DateRangeValue = [Date | null, Date | null];

export type ReactRangeProps = {
  days?: number;
  error?: React.ReactNode;
  disabled?: boolean;
  label?: React.ReactNode;
  lastDay?: moment.Moment;
  formatLabel?: (v: moment.Moment) => string;
  onChange: (value: [Date | null, Date | null]) => any;
  // default true
  showHandleTooltipDragging?: boolean;
  // default undefined
  showHandleTooltipAlways?: boolean;
  value: DateRangeValue;
  withLabels?: boolean;
};

type Props = ReactRangeProps & {
  classes: Classes<typeof styles>;
  theme: ThemeType;
};

type State = {
  isStartDatePickerOpen: boolean;
  isEndDatePickerOpen: boolean;
  value: [number, number];
};
class DateRangeCmp extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    days: 365,
    lastDay: moment(),
    showHandleTooltipDragging: true,
    withLabels: true,
    formatLabel: (x) => x.format("L")
  };

  static getDerivedStateFromProps(props: Props, state) {
    return {
      ...state,
      value: [
        dateToValue(props.value[0], props.days, props.lastDay, "end"),
        dateToValue(props.value[1], props.days, props.lastDay, "start")
      ]
    };
  }

  startLabelRef = React.createRef<HTMLDivElement>();
  endLabelRef = React.createRef<HTMLDivElement>();

  constructor(props) {
    super(props);

    this.state = {
      isEndDatePickerOpen: false,
      isStartDatePickerOpen: false,
      value: props.value
    };
  }

  renderHandle = (handleProps) => {
    log("renderHandle", handleProps);

    const { lastDay, days, showHandleTooltipAlways, showHandleTooltipDragging } =
      this.props;
    const { value: handleValue, dragging, index, ...restProps } = handleProps;

    return (
      <Tooltip
        title={valueToDateString(
          handleValue,
          lastDay,
          days,
          "start",
          this.props.formatLabel
        )}
        open={showHandleTooltipAlways || (showHandleTooltipDragging && dragging)}
        placement="top"
        key={index}
      >
        <Handle value={handleValue} {...restProps} />
      </Tooltip>
    );
  };

  renderStartDatePicker = () => {
    if (!this.startLabelRef) {
      return null;
    }

    const { days, lastDay, onChange } = this.props;
    const { isStartDatePickerOpen, value } = this.state;
    const [end, start] = value;

    return (
      <Popover
        anchorEl={this.startLabelRef.current}
        open={isStartDatePickerOpen}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <ClickAwayListener
          onClickAway={() => this.setState({ isStartDatePickerOpen: false })}
        >
          <ReactDatePicker
            inline
            onChange={(v) =>
              onChange([
                valueToDate(end, lastDay, days),
                pickerDateToDate(moment(v), lastDay, days)
              ])
            }
            minDate={moment(lastDay).subtract(days, "d").toDate()}
            maxDate={moment(lastDay).subtract(days - end, "d").toDate()}
            selected={moment(lastDay).subtract(days - start, "d").toDate()}
            openToDate={moment(lastDay).subtract(days - start, "d").toDate()}
          />
        </ClickAwayListener>
      </Popover>
    );
  };

  renderEndDatePicker = () => {
    if (!this.endLabelRef) {
      return null;
    }

    const { days, lastDay, onChange } = this.props;
    const { isEndDatePickerOpen, value } = this.state;
    const [end, start] = value;

    return (
      <Popover
        anchorEl={this.endLabelRef.current}
        open={isEndDatePickerOpen}
        anchorOrigin={{ horizontal: "center", vertical: "bottom" }}
        transformOrigin={{ horizontal: "center", vertical: "top" }}
      >
        <ClickAwayListener
          onClickAway={() => this.setState({ isEndDatePickerOpen: false })}
        >
          <ReactDatePicker
            inline
            onChange={(v) =>
              onChange([
                pickerDateToDate(moment(v), lastDay, days),
                valueToDate(start, lastDay, days)
              ])
            }
            minDate={moment(lastDay).subtract(days - start, "d").toDate()}
            maxDate={lastDay.toDate()}
            selected={
              end < this.props.days ? moment(lastDay).subtract(days - end, "d").toDate() : null
            }
            openToDate={moment(lastDay).subtract(days - end, "d").toDate()}
          />
        </ClickAwayListener>
      </Popover>
    );
  };

  renderStartLabel = () => {
    const { classes, days, lastDay, withLabels } = this.props;
    const { value } = this.state;

    const v = value[1];

    if (!withLabels) {
      return null;
    }

    return (
      <div
        ref={this.startLabelRef}
        onClick={() => this.setState({ isStartDatePickerOpen: true })}
      >
        <Small className={classes.staticLabel}>
          {valueToDateString(v, lastDay, days, "start", this.props.formatLabel)}
          {!v && (
            <span>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>
          )}
          {/* <span>&nbsp;</span> dirty solution only to make text as large as
            date string so the slider doesn't flip out in the edge */}
        </Small>
      </div>
    );
  };

  renderEndLabel = () => {
    const { classes, days, lastDay, withLabels } = this.props;
    const { value } = this.state;

    const v = value[0];

    if (!withLabels) {
      return null;
    }

    return (
      <div
        ref={this.endLabelRef}
        onClick={() => this.setState({ isEndDatePickerOpen: true })}
      >
        <Small className={classes.staticLabel}>
          {valueToDateString(v, lastDay, days, "end", this.props.formatLabel)}
        </Small>
      </div>
    );
  };

  render() {
    const {
      classes,
      days,
      disabled,
      error,
      lastDay,
      label,
      onChange,
      withLabels,
      theme
    } = this.props;

    const { value } = this.state;

    return (
      <div className={classes.root}>
        {label && (
          <InputLabel shrink disabled={disabled} error={Boolean(error)}>
            {label}
          </InputLabel>
        )}

        <div className={classes.rangeWrapper}>
          {this.renderStartLabel()}
          {this.renderStartDatePicker()}

          <div
            className={cns({
              [classes.rangeRoot]: !withLabels,
              [classes.rangeRootWithStaticLabels]: withLabels
            })}
          >
            <RCRange
              min={0}
              max={days}
              onChange={([start, end]) => {
                log("onChange", start, end);
                onChange([
                  valueToDate(end, lastDay, days),
                  valueToDate(start, lastDay, days)
                ]);
              }}
              value={[value[1], value[0]]}
              handle={this.renderHandle}
              disabled={disabled}
              trackStyle={[{ backgroundColor: theme.palette.primary.main }]}
              handleStyle={[
                {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.contrast
                },
                {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.primary.contrast
                }
              ]}
            />
          </div>

          {this.renderEndLabel()}
          {this.renderEndDatePicker()}
        </div>
      </div>
    );
  }
}

const styles = (theme: ThemeType) => ({
  root: {
    height: 48,
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between"
  },

  rangeWrapper: {
    display: "flex"
  },

  rangeRoot: {
    width: "98%",
    marginLeft: "1%"
  },

  rangeRootWithStaticLabels: {
    marginLeft: "3%",
    width: "94%",
    marginRight: "3%"
  },

  staticLabel: {
    minWidth: 64,
    cursor: "pointer",
    whiteSpace: "nowrap"
  }
});

export const DateRange = withStyles(styles as any, { withTheme: true })(
  DateRangeCmp
) as React.FC<ReactRangeProps>;
